import { Body, Heading, HtmlContent, ProgrammePart, Tag, TextButton } from 'designsystem';
import { ComponentProps, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { getMetaForFilmOrProjectHit, slugifyTitle, useGetApiImageProps } from 'shared';
import slugify from 'slugify';
import { FestivalFilmDetailsFragment } from '../gql/api';
import Link from 'next/link';

interface Props extends Partial<ComponentProps<typeof ProgrammePart>> {
    film: FestivalFilmDetailsFragment;
}

const FilmProgrammePart: React.FC<Props> = ({ film, ...rest }) => {
    const getImageProps = useGetApiImageProps();
    const section = film?.sections?.[0];
    const filmSectionItems = section?.items;
    const slides: ComponentProps<typeof ProgrammePart>['slides'] = useMemo(
        () =>
            filmSectionItems?.length > 0
                ? filmSectionItems
                      .map(item => {
                          if (item.__typename === 'Film') {
                              return {
                                  key: item.id,
                                  image: getImageProps(item.publications?.favoriteImage, item.fullPreferredTitle),
                                  title: item.fullPreferredTitle,
                                  meta: {
                                      ...getMetaForFilmOrProjectHit({
                                          filmOrProject: item,
                                          filmOrProjectType: 'schedule',
                                      }),
                                      tag: item.isDocLab && <Tag>DocLab</Tag>,
                                  },
                                  href: `/film/${item.id}/${slugifyTitle(item.fullPreferredTitle)}`,
                              };
                          }
                          if (item.__typename === 'Composition') {
                              return {
                                  key: item.id,
                                  title: item.fullTitle,
                                  image: getImageProps(item.publications?.favoriteImage, item.fullTitle),
                                  href: `/composition/${item.id}/${slugifyTitle(item.fullTitle)}`,
                              };
                          }
                          // should never happen
                          return null;
                      })
                      .filter(Boolean)
                : null,
        [filmSectionItems, getImageProps]
    );

    return (
        slides && (
            <ProgrammePart slides={slides} mb={[-8, -10, null, -18]} mt={18} {...rest}>
                <Heading variant={2}>
                    <FormattedMessage
                        defaultMessage="More from the program section {section}"
                        values={{
                            section: film.sections?.[0]?.name,
                        }}
                    />
                </Heading>
                {section?.description?.translation && (
                    <Body>
                        <HtmlContent html={section.description.translation} />
                    </Body>
                )}
                <TextButton as={Link} size="s" href={`/section/${section.id}/${slugify(section.name)}`}>
                    <FormattedMessage defaultMessage="View program section" />
                </TextButton>
            </ProgrammePart>
        )
    );
};

export default FilmProgrammePart;
